import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiEdit,
  FiLayers,
  FiDollarSign,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiPlus,
  FiFilter,
  FiAlertTriangle,
  FiZap,
  FiInbox,
  FiBarChart2,
  FiShield,
  FiClipboard,
  FiRadio,
  FiDownload,
  FiArchive,
  FiPrinter,
  FiInfo,
  FiTrash2,
} from 'react-icons/fi';
import { MdPix } from 'react-icons/md';
import { FaBarcode } from 'react-icons/fa';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI, clientesAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    gap: ${theme.spacing[4]};
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  min-width: 0;
  overflow: hidden;
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  min-width: 0;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
`;

const StatusBadge = styled(Badge)`
  color: ${({ $status }) => {
    switch ($status) {
      case 'ATIVO':
        return theme.colors.success[600];
      case 'CANCELADO':
        return theme.colors.error[600];
      case 'EXPIRADO':
        return theme.colors.warning[700];
      default:
        return theme.colors.text.secondary;
    }
  }};
  background: ${({ $status }) => {
    switch ($status) {
      case 'ATIVO':
        return theme.colors.success[100];
      case 'CANCELADO':
        return theme.colors.error[100];
      case 'EXPIRADO':
        return theme.colors.warning[100];
      default:
        return theme.colors.background.primary;
    }
  }};
  text-transform: capitalize;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing[4]};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
`;

const InfoLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const InfoValue = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-height: 440px;
  overflow-y: auto;
`;

const CycleItem = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.primary[500] : theme.colors.border.primary)};
  background: ${({ $active }) => ($active ? theme.colors.primary[500] + '15' : theme.colors.background.primary)};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  cursor: pointer;
  align-items: flex-start;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
  }
`;

const CycleTitle = styled.span`
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const CycleSubtitle = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  display: flex;
  align-items: center;
  width: 100%;
  
  > span:first-child {
    flex: 0 0 auto;
    margin-right: auto;
  }
  
  > span:last-child {
    flex: 0 0 auto;
    margin-left: auto;
  }
`;

const CycleDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const MetricLabel = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`;

const MetricValue = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  background: ${theme.colors.background.muted};
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    min-width: 400px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.muted};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.text.muted};
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.text.primary};
    background: ${theme.colors.background.primary};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${(props) => (props.$danger ? theme.colors.error[500] : theme.colors.border.primary)};
  background: ${(props) => (props.$danger ? theme.colors.error[500] : 'transparent')};
  color: ${(props) => (props.$danger ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${(props) => (props.$danger ? theme.colors.error[600] : theme.colors.background.secondary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing[3]};
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) => {
      switch ($type) {
        case 'error':
          return theme.colors.error[500];
        case 'success':
          return theme.colors.success[500];
        case 'info':
          return theme.colors.primary[400];
        default:
          return theme.colors.warning[500];
      }
    }};
  background: ${({ $type }) => {
    switch ($type) {
      case 'error':
        return theme.colors.error[100];
      case 'success':
        return theme.colors.success[100];
      case 'info':
        return theme.colors.primary[50];
      default:
        return theme.colors.warning[100];
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'error':
        return theme.colors.error[700];
      case 'success':
        return theme.colors.success[700];
      case 'info':
        return theme.colors.primary[700];
      default:
        return theme.colors.warning[700];
    }
  }};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const ExtensaoStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    // Status da extensão (contrato_extensoes)
    if (normalized === 'CONFIRMADO') return theme.colors.success[100];
    // Status da cobrança (extensao_cobrancas)
    if (normalized === 'RECEIVED' || normalized === 'CONFIRMED') return theme.colors.success[100];
    if (normalized === 'OVERDUE') return theme.colors.error[100];
    return theme.colors.warning[100];
  }};
  color: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    // Status da extensão (contrato_extensoes)
    if (normalized === 'CONFIRMADO') return theme.colors.success[700];
    // Status da cobrança (extensao_cobrancas)
    if (normalized === 'RECEIVED' || normalized === 'CONFIRMED') return theme.colors.success[700];
    if (normalized === 'OVERDUE') return theme.colors.error[700];
    return theme.colors.warning[700];
  }};
`;

const ResultadoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ResultadoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
`;

const ResultadoLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const ResultadoLabel = styled.span`
  font-size: ${theme.fontSizes.xs};
  text-transform: uppercase;
  font-weight: 700;
  color: ${theme.colors.text.muted};
`;

const ResultadoValue = styled.span`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const CopyField = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[3]};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: ${theme.fontSizes.sm};
  word-break: break-all;
  background: ${theme.colors.background.secondary};
`;

const CopyButtonSmall = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  border: none;
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.background.primary};
  font-weight: 600;
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};

  &:hover {
    background: ${theme.colors.primary[600]};
  }
`;

const PixWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  align-items: center;
`;

const PixImage = styled.img`
  width: 160px;
  height: 160px;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: #fff;
  object-fit: cover;
`;

const PixInfo = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const InvoiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  color: ${theme.colors.primary[600]};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.fontSizes.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.background.primary};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  position: relative;
`;

const ChartSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

const Tooltip = styled.div`
  position: absolute;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 1000;
  font-size: ${theme.fontSizes.sm};
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing[1]};
`;

const TooltipValue = styled.div`
  font-weight: 700;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'UTC',
      }).format(new Date(value))
    : '—';

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const agruparConsumoPorDia = (consumos, dataInicio) => {
  if (!dataInicio) return [];
  
  // Parsear data de início usando UTC para evitar problemas de timezone
  const dataInicioStr = typeof dataInicio === 'string' && dataInicio.includes('T') 
    ? dataInicio.split('T')[0] 
    : dataInicio;
  const [anoInicio, mesInicio, diaInicio] = dataInicioStr.split('-').map(Number);
  const inicio = new Date(Date.UTC(anoInicio, mesInicio - 1, diaInicio));
  
  // Data de hoje em UTC (banco de dados usa UTC)
  const agora = new Date();
  const hoje = new Date(Date.UTC(
    agora.getUTCFullYear(),
    agora.getUTCMonth(),
    agora.getUTCDate()
  ));
  
  const consumoPorDia = new Map();
  
  if (consumos && consumos.length > 0) {
    consumos.forEach((consumo) => {
      if (!consumo.data) return;
      
      // Processar data em UTC (banco retorna timestamp em UTC)
      let dataConsumoUTC;
      if (typeof consumo.data === 'string') {
        // Se já tem timezone, usar diretamente; senão assumir UTC
        if (consumo.data.includes('T')) {
          dataConsumoUTC = new Date(consumo.data.endsWith('Z') ? consumo.data : consumo.data + 'Z');
        } else {
          // Formato YYYY-MM-DD, assumir UTC
          const [anoConsumo, mesConsumo, diaConsumo] = consumo.data.split('-').map(Number);
          dataConsumoUTC = new Date(Date.UTC(anoConsumo, mesConsumo - 1, diaConsumo));
        }
      } else {
        // Se for objeto Date, usar diretamente
        dataConsumoUTC = new Date(consumo.data);
      }
      
      // Garantir que está em UTC
      const diaKey = new Date(Date.UTC(
        dataConsumoUTC.getUTCFullYear(),
        dataConsumoUTC.getUTCMonth(),
        dataConsumoUTC.getUTCDate()
      )).toISOString().split('T')[0];
      
      // Aceita tanto 'quantidade' quanto 'creditos' (para compatibilidade)
      const quantidade = Number.parseInt(consumo.quantidade || consumo.creditos || 0, 10);
      
      if (consumoPorDia.has(diaKey)) {
        consumoPorDia.set(diaKey, consumoPorDia.get(diaKey) + quantidade);
      } else {
        consumoPorDia.set(diaKey, quantidade);
      }
    });
  }
  
  // Retornar apenas os dias que têm consumo (quantidade > 0)
  const dias = [];
  consumoPorDia.forEach((quantidade, diaKey) => {
    if (quantidade > 0) {
      dias.push({
        data: diaKey,
        quantidade: quantidade,
      });
    }
  });
  
  // Ordenar por data para manter a ordem cronológica
  dias.sort((a, b) => {
    const dataA = new Date(a.data + 'T00:00:00Z');
    const dataB = new Date(b.data + 'T00:00:00Z');
    return dataA - dataB;
  });
  
  return dias;
};

const calcularMediaDiaria = (consumos, dataInicio) => {
  if (!consumos || consumos.length === 0) return 0;
  
  const total = consumos.reduce((acc, consumo) => {
    // Aceita tanto 'quantidade' quanto 'creditos' (para compatibilidade)
    return acc + Number.parseInt(consumo.quantidade || consumo.creditos || 0, 10);
  }, 0);
  
  // Parsear data de início em UTC
  const dataInicioStr = typeof dataInicio === 'string' && dataInicio.includes('T') 
    ? dataInicio.split('T')[0] 
    : dataInicio;
  const [anoInicio, mesInicio, diaInicio] = dataInicioStr.split('-').map(Number);
  const inicio = new Date(Date.UTC(anoInicio, mesInicio - 1, diaInicio));
  
  // Data de hoje em UTC (banco de dados usa UTC)
  const agora = new Date();
  const hoje = new Date(Date.UTC(
    agora.getUTCFullYear(),
    agora.getUTCMonth(),
    agora.getUTCDate()
  ));
  
  const diasDecorridos = Math.max(1, Math.ceil((hoje - inicio) / (1000 * 60 * 60 * 24)) + 1);
  
  return total / diasDecorridos;
};

const getExtensaoStatusLabel = (status, temCobranca = false) => {
  const normalized = (status || '').toUpperCase();
  
  // Sempre mostrar "Pago" quando confirmado, nunca mais "Liberado"
  if (normalized === 'RECEIVED' || normalized === 'CONFIRMED' || normalized === 'CONFIRMADO') {
    return 'Pago';
  }
  if (normalized === 'OVERDUE') {
    return 'Atrasado';
  }
  return 'Pendente';
};

const getCobrancaStatusLabel = (status) => {
  switch ((status || 'PENDING').toUpperCase()) {
    case 'RECEIVED':
    case 'CONFIRMED':
      return 'Pago';
    case 'OVERDUE':
      return 'Atrasado';
    default:
      return 'Pendente';
  }
};

const criarEstadoModalExtensao = () => ({
  aberto: false,
  cicloId: null,
  creditos: '',
  valor_pago: '',
  erro: null,
  salvando: false,
  resultado: null,
  formaPagamento: 'cartao_credito', // 'boleto_pix' ou 'cartao_credito'
});

const copiarConteudo = async (texto, label) => {
  if (!texto) return;
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado!`);
  } catch (error) {
    toast.error('Não foi possível copiar automaticamente. Copie manualmente.');
  }
};

const extrairPayloadPix = (pixQrCode) => {
  if (!pixQrCode) return null;
  
  if (typeof pixQrCode === 'string') {
    return pixQrCode;
  }
  
  if (typeof pixQrCode === 'object') {
    return pixQrCode.payload || pixQrCode.qrCode || null;
  }
  
  return null;
};

const baixarPDF = (url) => {
  if (!url) {
    toast.error('Link do PDF não disponível.');
    return;
  }
  window.open(url, '_blank');
};

const gerarCobrancaCicloManual = async (contratoId, cicloId, setGerando, carregarContrato) => {
  if (!contratoId || !cicloId) {
    toast.error('Dados do contrato ou ciclo não encontrados');
    return;
  }

  try {
    setGerando(true);
    const response = await contratosAPI.gerarCobrancaCiclo(contratoId, cicloId);
    
    if (response.data?.success) {
      toast.success('Cobrança gerada com sucesso!');
      // Recarregar contrato para atualizar dados do ciclo
      await carregarContrato();
    } else {
      toast.error(response.data?.error || 'Erro ao gerar cobrança');
    }
  } catch (error) {
    const mensagemErro = error.response?.data?.error || error.message || 'Erro ao gerar cobrança';
    toast.error(mensagemErro);
  } finally {
    setGerando(false);
  }
};

const getPixImageSrc = (pixQr) => {
  if (!pixQr) return null;

  const encodedImage =
    typeof pixQr === 'string'
      ? pixQr
      : typeof pixQr.encodedImage === 'string'
      ? pixQr.encodedImage
      : null;

  if (encodedImage && encodedImage.startsWith('data:')) {
    return encodedImage;
  }

  if (encodedImage) {
    return `data:image/png;base64,${encodedImage}`;
  }

  const qrCodeImage =
    typeof pixQr === 'string'
      ? pixQr
      : typeof pixQr.qrCodeImage === 'string'
      ? pixQr.qrCodeImage
      : null;

  return qrCodeImage;
};

const ContratoDetalhes = () => {
  const navigate = useNavigate();
  const params = useParams();
  const contratoId = params.id ? Number.parseInt(params.id, 10) : null;

  const [contrato, setContrato] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [exportandoTodos, setExportandoTodos] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [modalCiclo, setModalCiclo] = useState({
    aberto: false,
    inicio: '',
    fim: '',
    creditos_base: '',
    creditos_carryover: 0,
    erro: null,
    salvando: false,
  });
  const [modalExtensao, setModalExtensao] = useState(criarEstadoModalExtensao);
  const fecharModalExtensao = () => setModalExtensao(criarEstadoModalExtensao());
  const [modalConfirmarPagamento, setModalConfirmarPagamento] = useState({
    aberto: false,
    cobranca: null,
    valor: '',
    data: '',
    salvando: false,
    erro: null,
  });
  const fecharModalConfirmarPagamento = () => {
    setModalConfirmarPagamento({
      aberto: false,
      cobranca: null,
      valor: '',
      data: '',
      salvando: false,
      erro: null,
    });
  };
  const [modalExcluirCobranca, setModalExcluirCobranca] = useState({
    aberto: false,
    cobranca: null,
    excluindo: false,
  });
  const fecharModalExcluirCobranca = () => {
    setModalExcluirCobranca({
      aberto: false,
      cobranca: null,
      excluindo: false,
    });
  };
  const [gerandoCobrancaCiclo, setGerandoCobrancaCiclo] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null, valor: 0 });
  const chartContainerRef = useRef(null);

  const carregarContrato = async (silent = false) => {
    if (!contratoId) return;
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await contratosAPI.buscarPorId(contratoId);
      if (response.data?.success) {
        const dados = response.data.data;
        setContrato(dados);
        if (dados.ciclos && dados.ciclos.length > 0) {
          const cicloOrdenado = [...dados.ciclos].sort(
            (a, b) => new Date(b.inicio).getTime() - new Date(a.inicio).getTime(),
          );
          
          // Preservar o ciclo selecionado se ele ainda existir nos novos dados
          // Usar função de callback para acessar o valor atualizado do estado
          setSelectedCycleId((cicloIdAtual) => {
            if (cicloIdAtual) {
              const cicloSelecionadoAindaExiste = cicloOrdenado.some(ciclo => ciclo.id === cicloIdAtual);
              if (cicloSelecionadoAindaExiste) {
                // Manter o ciclo selecionado
                return cicloIdAtual;
              } else {
                // Se o ciclo selecionado não existe mais, selecionar o primeiro
                return cicloOrdenado[0].id;
              }
            } else {
              // Se não há ciclo selecionado, selecionar o primeiro
              return cicloOrdenado[0].id;
            }
          });
        } else {
          setSelectedCycleId(null);
        }
        // Carregar informações do cliente
        if (dados.cliente_id) {
          try {
            const clienteResponse = await clientesAPI.buscarPorId(dados.cliente_id);
            if (clienteResponse.data?.success) {
              setCliente(clienteResponse.data.data);
            }
          } catch (error) {
            console.error('Erro ao carregar cliente', error);
          }
        }
      } else {
        setErro('Contrato não encontrado.');
      }
    } catch (error) {
      console.error('Erro ao carregar contrato', error);
      setErro('Erro ao carregar contrato.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    carregarContrato();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId]);

  // Polling automático para atualizar status das extensões quando webhook atualizar
  useEffect(() => {
    if (!contratoId) return;

    // Atualizar a cada 10 segundos quando a página estiver visível
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        carregarContrato(true); // silent = true para não mostrar loading
      }
    }, 10000); // 10 segundos

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId]);

  // Função auxiliar para verificar se o contrato permite ações de edição/criação
  const permiteEdicao = useMemo(() => {
    if (!contrato) return false;
    // Apenas contratos ATIVOS permitem edição
    return contrato.status === 'ATIVO';
  }, [contrato]);

  const cicloSelecionado = useMemo(() => {
    if (!contrato || !Array.isArray(contrato.ciclos)) return null;
    return contrato.ciclos.find((ciclo) => ciclo.id === selectedCycleId) || contrato.ciclos[0] || null;
  }, [contrato, selectedCycleId]);

  const opcoesCreditos = useMemo(() => {
    if (!contrato || !contrato.creditos_mensais) return [];
    const creditosMensais = Number.parseInt(contrato.creditos_mensais, 10);
    if (!Number.isFinite(creditosMensais) || creditosMensais <= 0) return [];

    const opcoes = [];
    const percentuais = [0.1, 0.3, 0.5, 0.7, 1.0];
    
    for (const percentual of percentuais) {
      const valor = creditosMensais * percentual;
      const valorArredondado = Math.round(valor / 10) * 10;
      const valorFinal = Math.max(10, Math.min(creditosMensais, valorArredondado));
      if (!opcoes.includes(valorFinal)) {
        opcoes.push(valorFinal);
      }
    }

    return opcoes.sort((a, b) => a - b);
  }, [contrato]);

  const calcularValorExtensao = useMemo(() => {
    return (creditosSelecionados) => {
      if (!contrato || !creditosSelecionados) return '';
      const creditosInt = Number.parseInt(creditosSelecionados, 10);
      if (!Number.isFinite(creditosInt) || creditosInt <= 0) return '';

      const valorMensal = Number.parseFloat(contrato.valor_mensal);
      const creditosMensais = Number.parseInt(contrato.creditos_mensais, 10);
      
      if (!Number.isFinite(valorMensal) || !Number.isFinite(creditosMensais) || creditosMensais <= 0) {
        return '';
      }

      const valorComAcrescimo = valorMensal * 1.5;
      const valorPorCredito = valorComAcrescimo / creditosMensais;
      const valorTotal = valorPorCredito * creditosInt;
      
      const valorArredondado = Math.floor(valorTotal);
      const valorFinal = valorArredondado + 0.99;
      
      return valorFinal.toFixed(2);
    };
  }, [contrato]);

  const dadosGraficoConsumo = useMemo(() => {
    if (!cicloSelecionado || !cicloSelecionado.inicio) {
      return [];
    }
    const consumos = cicloSelecionado.consumos || [];
    const dados = agruparConsumoPorDia(consumos, cicloSelecionado.inicio);
    const agoraUTC = new Date();
    const hojeUTC = new Date(Date.UTC(
      agoraUTC.getUTCFullYear(),
      agoraUTC.getUTCMonth(),
      agoraUTC.getUTCDate()
    ));
    console.log('[DEBUG] Grafico - dados agrupados (UTC):', { 
      inicio: cicloSelecionado.inicio,
      dataHojeUTC: hojeUTC.toISOString().split('T')[0],
      totalConsumos: consumos.length,
      diasAgrupados: dados.length,
      primeiroDia: dados[0]?.data,
      ultimoDia: dados[dados.length - 1]?.data,
      primeirosDados: dados.slice(0, 5),
      ultimosDados: dados.slice(-5),
    });
    return dados;
  }, [cicloSelecionado]);

  const mediaDiariaConsumo = useMemo(() => {
    if (!cicloSelecionado || !cicloSelecionado.inicio || !cicloSelecionado.consumos) return 0;
    return calcularMediaDiaria(cicloSelecionado.consumos, cicloSelecionado.inicio);
  }, [cicloSelecionado]);

  useEffect(() => {
    if (modalExtensao.aberto && opcoesCreditos.length > 0 && !modalExtensao.creditos) {
      const primeiroCredito = String(opcoesCreditos[0]);
      const valorCalculado = calcularValorExtensao(primeiroCredito);
      setModalExtensao((prev) => ({
        ...prev,
        creditos: primeiroCredito,
        valor_pago: valorCalculado,
      }));
    } else if (modalExtensao.aberto && modalExtensao.creditos) {
      const valorCalculado = calcularValorExtensao(modalExtensao.creditos);
      if (valorCalculado && valorCalculado !== modalExtensao.valor_pago) {
        setModalExtensao((prev) => ({
          ...prev,
          valor_pago: valorCalculado,
      }));
    }
    }
  }, [modalExtensao.aberto, opcoesCreditos, modalExtensao.creditos, calcularValorExtensao]);

  const exportarParaPDF = () => {
    if (!contrato) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;

    // Cores
    const primaryColor = [66, 153, 225];
    const darkColor = [30, 41, 59];
    const lightGray = [203, 213, 225];
    const textColor = [51, 65, 85];
    const successColor = [34, 197, 94];

    // Função para adicionar nova página se necessário
    const checkNewPage = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Cabeçalho
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RELATÓRIO DE CONTRATO', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Contrato #${contrato.id}`, pageWidth / 2, 32, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, pageWidth / 2, 40, {
      align: 'center',
    });

    yPos = 55;

    // Informações do Cliente
    if (cliente) {
      checkNewPage(30);
      doc.setTextColor(...textColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('INFORMAÇÕES DO CLIENTE', margin, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      // Nome Fantasia
      if (cliente.nome_fantasia) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Nome Fantasia:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.nome_fantasia, margin + 45, yPos);
        yPos += 7;
      }
      
      // Nome Social
      if (cliente.nome_social) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Nome Social:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.nome_social, margin + 45, yPos);
        yPos += 7;
      }
      
      // Nome de Contato
      if (cliente.nome_contato) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Nome de Contato:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.nome_contato, margin + 45, yPos);
        yPos += 7;
      }
      
      // Telefone
      if (cliente.fone_contato) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Telefone:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.fone_contato, margin + 45, yPos);
        yPos += 7;
      }
      
      // E-mail
      if (cliente.emial_contato) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('E-mail:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.emial_contato, margin + 45, yPos);
        yPos += 7;
      }
      
      // Tipo e CPF/CNPJ na mesma linha
      if (cliente.tipo_pessoa || cliente.cpf_cnpj) {
        checkNewPage(8);
        // Posição fixa para alinhar CPF/CNPJ com o label CEP
        const cepLabelX = pageWidth * 0.7; // 70% da largura (mesma posição do label CEP)
        
        doc.setFont('helvetica', 'bold');
        doc.text('Tipo:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        const tipo = cliente.tipo_pessoa || '—';
        doc.text(tipo, margin + 45, yPos);
        
        if (cliente.cpf_cnpj) {
          doc.setFont('helvetica', 'bold');
          doc.text('CPF/CNPJ:', margin + 95, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.cpf_cnpj, cepLabelX, yPos);
        }
        yPos += 7;
      }
      
      // Cidade, Estado e CEP na mesma linha
      if (cliente.cidade || cliente.estado || cliente.cep) {
        checkNewPage(8);
        // Posições fixas baseadas na largura da página
        const estadoLabelX = pageWidth * 0.55; // 55% da largura (mais afastado da Cidade)
        const estadoValueX = pageWidth * 0.62; // 62% da largura (mais espaçado do label)
        const cepLabelX = pageWidth * 0.7; // 70% da largura
        const cepValueX = pageWidth * 0.75; // 75% da largura
        
        doc.setFont('helvetica', 'bold');
        doc.text('Cidade:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(cliente.cidade || '—', margin + 45, yPos);
        
        if (cliente.estado) {
          doc.setFont('helvetica', 'bold');
          doc.text('Estado:', estadoLabelX, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.estado, estadoValueX, yPos);
        }
        
        if (cliente.cep) {
          doc.setFont('helvetica', 'bold');
          doc.text('CEP:', cepLabelX, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.cep, cepValueX, yPos);
        }
        yPos += 7;
      }
      
      // Endereço
      if (cliente.endereco) {
        checkNewPage(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Endereço:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        const maxWidth = pageWidth - margin - 55;
        const lines = doc.splitTextToSize(cliente.endereco, maxWidth);
        doc.text(lines, margin + 45, yPos);
        yPos += lines.length * 7;
      }
      
      yPos += 5;
    }

    // Informações do Contrato
    checkNewPage(30);
    doc.setDrawColor(...lightGray);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    doc.setTextColor(...textColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMAÇÕES DO CONTRATO', margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Status com cor
    const statusColor = contrato.status === 'ATIVO' ? successColor : textColor;
    doc.setTextColor(...statusColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Status:', margin, yPos);
    doc.text(contrato.status || '—', margin + 45, yPos);
    yPos += 7;
    
    doc.setTextColor(...textColor);
    const infoContrato = [
      [`ID do Contrato:`, `#${contrato.id}`],
      [`Nome do Plano:`, contrato.nome_plano || '—'],
      [`Data de Início:`, formatDate(contrato.data_inicio)],
      [`Data de Término:`, contrato.data_fim ? formatDate(contrato.data_fim) : 'Indeterminado'],
      [`Valor Mensal:`, formatCurrency(contrato.valor_mensal)],
      [`Créditos Mensais:`, `${contrato.creditos_mensais || 0} créditos`],
      [`Período de Renovação:`, `${contrato.renovacao_meses || 0} meses`],
    ];

    infoContrato.forEach(([label, value]) => {
      checkNewPage(8);
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, margin + 45, yPos);
      yPos += 7;
    });

    yPos += 5;

    // Recursos do Contrato
    if (contrato.recursos && contrato.recursos.length > 0) {
      checkNewPage(20);
      doc.setDrawColor(...lightGray);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('RECURSOS INCLUÍDOS', margin, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      contrato.recursos.forEach((recurso) => {
        checkNewPage(8);
        doc.text(`• ${recurso.descricao || '—'}`, margin + 5, yPos);
        yPos += 7;
      });
      yPos += 5;
    }

    // Ciclos do Contrato
    if (contrato.ciclos && contrato.ciclos.length > 0) {
      checkNewPage(25);
      doc.setDrawColor(...lightGray);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('CICLOS DO CONTRATO', margin, yPos);
      yPos += 10;

      // Ciclos já vêm ordenados do mais recente para o mais antigo
      // Mas a numeração deve ser: mais antigo = Ciclo 1, mais recente = Ciclo N
      contrato.ciclos.forEach((ciclo, index) => {
        checkNewPage(60);
        
        // Box do ciclo
        doc.setDrawColor(...primaryColor);
        doc.setLineWidth(0.5);
        doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 5, 'F');
        doc.setDrawColor(...lightGray);
        doc.setLineWidth(0.1);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 1, 'F');
        yPos += 8;

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        // Numeração reversa: o último (mais antigo) é Ciclo 1, o primeiro (mais recente) é Ciclo N
        const numeroCiclo = contrato.ciclos.length - index;
        doc.text(`Ciclo ${numeroCiclo}`, margin, yPos);
        yPos += 8;

        doc.setTextColor(...textColor);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        // Calcular custos
        const custoPlano = contrato.valor_mensal || 0;
        const custoExtensoes = ciclo.extensoes 
          ? ciclo.extensoes.reduce((acc, ext) => acc + (Number.parseFloat(ext.valor_pago) || 0), 0)
          : 0;
        const custoTotal = custoPlano + custoExtensoes;
        const custoPorCredito = ciclo.creditos_totais > 0 
          ? custoTotal / ciclo.creditos_totais 
          : 0;
        
        const infoCiclo = [
          [`Período:`, `${formatDate(ciclo.inicio)} - ${formatDate(ciclo.fim)}`],
          [`Créditos Base:`, `${ciclo.creditos_base || 0} créditos`],
          [`Créditos Carryover:`, `${ciclo.creditos_carryover || 0} créditos`],
          [`Créditos de Extensões:`, `${ciclo.creditos_extensoes || 0} créditos`],
          [`Créditos Totais:`, `${ciclo.creditos_totais || 0} créditos`],
          [`Créditos Utilizados:`, `${ciclo.creditos_usados || 0} créditos`],
          [`Saldo Atual:`, `${ciclo.creditos_saldo || 0} créditos`],
        ];

        infoCiclo.forEach(([label, value]) => {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text(label, margin + 5, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(value, margin + 50, yPos);
          yPos += 7;
        });
        
        // Informações de custo
        yPos += 3;
        checkNewPage(15);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text('Custos do Ciclo:', margin + 5, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', 'normal');
        
        const custosCiclo = [
          [`Custo do Plano:`, formatCurrency(custoPlano)],
        ];
        
        if (custoExtensoes > 0) {
          custosCiclo.push([`Custo de Extensões:`, formatCurrency(custoExtensoes)]);
        }
        
        custosCiclo.push([`Custo por Crédito:`, formatCurrency(custoPorCredito)]);
        
        custosCiclo.forEach(([label, value]) => {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text(label, margin + 5, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(value, margin + 50, yPos);
          yPos += 7;
        });

        // Extensões do Ciclo
        if (ciclo.extensoes && ciclo.extensoes.length > 0) {
          yPos += 5;
          checkNewPage(15);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Extensões Registradas:', margin + 5, yPos);
          yPos += 8;

          // Cabeçalho da tabela
          doc.setFillColor(...lightGray);
          const tableWidth = pageWidth - 2 * margin - 10;
          const tableStartX = margin + 5;
          const colWidth = tableWidth / 3;
          const headerHeight = 5;
          const headerTop = yPos - headerHeight;
          doc.rect(tableStartX, headerTop, tableWidth, headerHeight, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const headerCenterY = headerTop + headerHeight / 2;
          doc.text('Data', tableStartX + colWidth / 2, headerCenterY, { align: 'center' });
          doc.text('Créditos', tableStartX + colWidth + colWidth / 2, headerCenterY, { align: 'center' });
          doc.text('Valor Pago', tableStartX + 2 * colWidth + colWidth / 2, headerCenterY, { align: 'center' });
          yPos += 7;

          ciclo.extensoes.forEach((extensao) => {
            checkNewPage(8);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(formatDate(extensao.data), tableStartX + colWidth / 2, yPos, { align: 'center' });
            doc.text(`${extensao.creditos} créditos`, tableStartX + colWidth + colWidth / 2, yPos, { align: 'center' });
            doc.text(formatCurrency(extensao.valor_pago), tableStartX + 2 * colWidth + colWidth / 2, yPos, { align: 'center' });
            yPos += 7;
          });
        }

        // Consumos do Ciclo
        if (ciclo.consumos && ciclo.consumos.length > 0) {
          yPos += 5;
          checkNewPage(15);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Consumos Registrados:', margin + 5, yPos);
          yPos += 8;

          // Cabeçalho da tabela
          doc.setFillColor(...lightGray);
          const consumoTableWidth = pageWidth - 2 * margin - 10;
          const consumoTableStartX = margin + 5;
          const consumoColWidth = consumoTableWidth / 2;
          const consumoHeaderHeight = 5;
          const consumoHeaderTop = yPos - consumoHeaderHeight;
          doc.rect(consumoTableStartX, consumoHeaderTop, consumoTableWidth, consumoHeaderHeight, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const consumoHeaderCenterY = consumoHeaderTop + consumoHeaderHeight / 2;
          doc.text('Data', consumoTableStartX + consumoColWidth / 2, consumoHeaderCenterY, { align: 'center' });
          doc.text('Créditos Utilizados', consumoTableStartX + consumoColWidth + consumoColWidth / 2, consumoHeaderCenterY, { align: 'center' });
          yPos += 7;

          ciclo.consumos.forEach((consumo) => {
            checkNewPage(8);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(formatDate(consumo.data), consumoTableStartX + consumoColWidth / 2, yPos, { align: 'center' });
            doc.text(`${consumo.creditos || 0} créditos`, consumoTableStartX + consumoColWidth + consumoColWidth / 2, yPos, { align: 'center' });
            yPos += 7;
          });
        }

        yPos += 8;
      });
    }

    // Rodapé em todas as páginas
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Linha do rodapé
      doc.setDrawColor(...lightGray);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      
      // Texto do rodapé
      doc.setFontSize(8);
      doc.setTextColor(...textColor);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Página ${i} de ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' },
      );
      
      // Informação adicional
      doc.setFontSize(7);
      doc.setTextColor(...textColor);
      doc.text(
        `Haevo Revenda - Sistema de Gestão`,
        margin,
        pageHeight - 10,
      );
    }

    // Salvar PDF
    const nomeArquivo = contrato.nome_plano
      ? contrato.nome_plano.replace(/[^a-zA-Z0-9]/g, '_')
      : 'sem_nome';
    const fileName = `Contrato_${contrato.id}_${nomeArquivo}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const exportarTodosContratosCliente = async () => {
    if (!contrato || !contrato.cliente_id) return;

    try {
      setExportandoTodos(true);

      // Buscar todos os contratos do cliente
      const response = await contratosAPI.dashboardCliente(contrato.cliente_id);
      if (!response.data?.success || !response.data?.data?.contratos) {
        throw new Error('Não foi possível carregar os contratos do cliente.');
      }

      const contratosCliente = response.data.data.contratos || [];
      
      if (contratosCliente.length === 0) {
        alert('O cliente não possui contratos cadastrados.');
        return;
      }

      // Buscar detalhes completos de cada contrato (incluindo recursos e ciclos detalhados)
      const contratosCompletos = await Promise.all(
        contratosCliente.map(async (contratoItem) => {
          try {
            const detalhesResponse = await contratosAPI.buscarPorId(contratoItem.id);
            if (detalhesResponse.data?.success) {
              return detalhesResponse.data.data;
            }
            return contratoItem;
          } catch (error) {
            console.error(`Erro ao buscar detalhes do contrato ${contratoItem.id}:`, error);
            return contratoItem;
          }
        })
      );

      // Criar PDF com todos os contratos
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;

      // Cores
      const primaryColor = [66, 153, 225];
      const darkColor = [30, 41, 59];
      const lightGray = [203, 213, 225];
      const textColor = [51, 65, 85];
      const successColor = [34, 197, 94];
      const errorColor = [239, 68, 68];
      const warningColor = [245, 158, 11];

      // Função para adicionar nova página se necessário
      const checkNewPage = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Cabeçalho principal
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('RELATÓRIO DE TODOS OS CONTRATOS', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const nomeCliente = cliente?.nome_fantasia || cliente?.nome_social || `Cliente ${contrato.cliente_id}`;
      doc.text(nomeCliente, pageWidth / 2, 32, { align: 'center' });
      doc.setFontSize(9);
      doc.text(
        `Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
        pageWidth / 2,
        40,
        { align: 'center' },
      );

      yPos = 55;

      // Informações do Cliente (uma vez no início)
      if (cliente) {
        checkNewPage(30);
        doc.setTextColor(...textColor);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('INFORMAÇÕES DO CLIENTE', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        if (cliente.nome_fantasia) {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text('Nome Fantasia:', margin, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.nome_fantasia, margin + 45, yPos);
          yPos += 7;
        }

        if (cliente.nome_social) {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text('Nome Social:', margin, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.nome_social, margin + 45, yPos);
          yPos += 7;
        }

        if (cliente.cpf_cnpj) {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text('CPF/CNPJ:', margin, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(cliente.cpf_cnpj, margin + 45, yPos);
          yPos += 7;
        }

        yPos += 5;

        // Separador após informações do cliente
        checkNewPage(10);
        doc.setDrawColor(...lightGray);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      }

      // Recursos Incluídos (consolidado de todos os contratos do cliente)
      const todosRecursos = new Map();
      contratosCompletos.forEach((contratoItem) => {
        if (contratoItem.recursos && Array.isArray(contratoItem.recursos)) {
          contratoItem.recursos.forEach((recurso) => {
            // Usar ID do recurso como chave para evitar duplicatas
            const chave = recurso.id || recurso.descricao;
            if (chave && !todosRecursos.has(chave)) {
              todosRecursos.set(chave, recurso);
            }
          });
        }
      });

      if (todosRecursos.size > 0) {
        checkNewPage(20);
        doc.setTextColor(...textColor);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('RECURSOS INCLUÍDOS', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        Array.from(todosRecursos.values()).forEach((recurso) => {
          checkNewPage(8);
          doc.text(`• ${recurso.descricao || '—'}`, margin + 5, yPos);
          yPos += 7;
        });

        yPos += 5;

        // Separador após recursos
        checkNewPage(10);
        doc.setDrawColor(...lightGray);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      }

      // Exportar cada contrato
      contratosCompletos.forEach((contratoItem, index) => {
        // Adicionar separador antes de cada contrato (exceto o primeiro)
        if (index > 0) {
          checkNewPage(30);
          doc.setDrawColor(...lightGray);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 15;
        }

        // Título do Contrato
        checkNewPage(30);
        doc.setTextColor(...textColor);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(`CONTRATO #${contratoItem.id}`, margin, yPos);
        yPos += 8;

        // Status do contrato
        doc.setFontSize(10);
        const statusColor =
          contratoItem.status === 'ATIVO'
            ? successColor
            : contratoItem.status === 'CANCELADO'
              ? errorColor
              : warningColor;
        doc.setTextColor(...statusColor);
        doc.setFont('helvetica', 'bold');
        doc.text('Status:', margin, yPos);
        doc.text(contratoItem.status || '—', margin + 45, yPos);
        yPos += 7;

        // Informações básicas do contrato
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', 'normal');
        const infoContrato = [
          [`Nome do Plano:`, contratoItem.nome_plano || '—'],
          [`Data de Início:`, formatDate(contratoItem.data_inicio)],
          [`Data de Término:`, contratoItem.data_fim ? formatDate(contratoItem.data_fim) : 'Indeterminado'],
          [`Valor Mensal:`, formatCurrency(contratoItem.valor_mensal)],
          [`Créditos Mensais:`, `${contratoItem.creditos_mensais || 0} créditos`],
          [`Período de Renovação:`, `${contratoItem.renovacao_meses || 0} meses`],
        ];

        infoContrato.forEach(([label, value]) => {
          checkNewPage(8);
          doc.setFont('helvetica', 'bold');
          doc.text(label, margin, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(value, margin + 45, yPos);
          yPos += 7;
        });

        yPos += 5;

        // Ciclos do Contrato (resumo)
        if (contratoItem.ciclos && contratoItem.ciclos.length > 0) {
          checkNewPage(20);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('CICLOS MENSAIS', margin, yPos);
          yPos += 8;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          // Mostrar apenas os últimos 5 ciclos para não sobrecarregar o PDF
          const ciclosParaMostrar = contratoItem.ciclos.slice(0, 5);
          ciclosParaMostrar.forEach((ciclo) => {
            checkNewPage(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Ciclo ${formatDate(ciclo.inicio)} - ${formatDate(ciclo.fim)}:`, margin, yPos);
            yPos += 7;
            doc.setFont('helvetica', 'normal');
            doc.text(
              `  Saldo: ${ciclo.creditos_saldo} / ${ciclo.creditos_totais} créditos | Utilizados: ${ciclo.creditos_usados || 0}`,
              margin + 5,
              yPos,
            );
            yPos += 7;
          });

          if (contratoItem.ciclos.length > 5) {
            checkNewPage(8);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(...textColor);
            doc.text(
              `... e mais ${contratoItem.ciclos.length - 5} ciclo(s)`,
              margin + 5,
              yPos,
            );
            yPos += 7;
          }

          yPos += 5;
        }
      });

      // Rodapé em todas as páginas
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        doc.setDrawColor(...lightGray);
        doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

        doc.setFontSize(8);
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', 'normal');
        doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

        doc.setFontSize(7);
        doc.text('Haevo Revenda - Sistema de Gestão', margin, pageHeight - 10);
      }

      // Salvar PDF
      const nomeClienteArquivo = cliente?.nome_fantasia || cliente?.nome_social || `Cliente_${contrato.cliente_id}`;
      const nomeArquivoLimpo = nomeClienteArquivo.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Todos_Contratos_${nomeArquivoLimpo}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Erro ao exportar todos os contratos:', error);
      alert(`Erro ao exportar contratos: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setExportandoTodos(false);
    }
  };

  const criarCiclo = async () => {
    if (!contratoId) return;
    const creditosBaseInt = Number.parseInt(modalCiclo.creditos_base, 10);
    if (!modalCiclo.inicio || !modalCiclo.fim || Number.isNaN(creditosBaseInt)) {
      setModalCiclo((prev) => ({
        ...prev,
        erro: 'Preencha início, fim e créditos base corretamente.',
      }));
      return;
    }

    try {
      setModalCiclo((prev) => ({ ...prev, salvando: true, erro: null }));
      const payload = {
        inicio: modalCiclo.inicio,
        fim: modalCiclo.fim,
        creditos_base: creditosBaseInt,
        creditos_carryover: Number.parseInt(modalCiclo.creditos_carryover || 0, 10),
      };
      const response = await contratosAPI.criarCiclo(contratoId, payload);
      if (response.data?.success) {
        await carregarContrato(true);
        setModalCiclo({
          aberto: false,
          inicio: '',
          fim: '',
          creditos_base: '',
          creditos_carryover: 0,
          erro: null,
          salvando: false,
        });
      } else {
        throw new Error(response.data?.error || 'Não foi possível criar o ciclo.');
      }
    } catch (error) {
      console.error('Erro ao criar ciclo', error);
      setModalCiclo((prev) => ({
        ...prev,
        salvando: false,
        erro: error.response?.data?.error || 'Erro ao criar ciclo.',
      }));
    }
  };

  const adicionarExtensao = async () => {
    if (!contratoId || !modalExtensao.cicloId) return;
    const creditosInt = Number.parseInt(modalExtensao.creditos, 10);
    const valorFloat = Number.parseFloat(modalExtensao.valor_pago);
    if (Number.isNaN(creditosInt) || creditosInt <= 0 || Number.isNaN(valorFloat) || valorFloat < 0) {
      setModalExtensao((prev) => ({
        ...prev,
        erro: 'Informe créditos (>0) e valor válido.',
      }));
      return;
    }

    try {
      setModalExtensao((prev) => ({ ...prev, salvando: true, erro: null }));
      const payload = {
        creditos: creditosInt,
        cicloId: modalExtensao.cicloId,
        forma_pagamento: modalExtensao.formaPagamento,
      };
      const response = await contratosAPI.solicitarExtensao(contratoId, payload);
      if (response.data?.success) {
        toast.success('Cobrança criada com sucesso. Acompanhe os dados abaixo.');
        setModalExtensao((prev) => ({
          ...prev,
          resultado: response.data.data,
          salvando: false,
        }));
        await carregarContrato(true);
      } else {
        throw new Error(response.data?.error || 'Não foi possível criar a extensão.');
      }
    } catch (error) {
      console.error('Erro ao adicionar extensão', error);
      setModalExtensao((prev) => ({
        ...prev,
        salvando: false,
        erro: error.response?.data?.error || 'Erro ao adicionar extensão.',
      }));
    }
  };

  const linkPagamentoModalExtensao = modalExtensao.resultado
    ? modalExtensao.resultado.bankSlipUrl || modalExtensao.resultado.invoiceUrl || null
    : null;

  if (loading) {
    return (
      <Layout title="Detalhes do contrato">
        <Container>
          <Card>
            <Subtitle>Carregando informações do contrato...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  if (erro || !contrato) {
    return (
      <Layout title="Detalhes do contrato">
        <Container>
          <Card>
            <Alert $type="error">
              <FiAlertTriangle />
              {erro || 'Contrato não encontrado.'}
            </Alert>
            <GhostButton onClick={() => navigate('/gestao/contratos')}>
              <FiArrowLeft />
              Voltar para contratos
            </GhostButton>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={`Contrato #${contrato.id}`}>
      <Container>
        <Header>
          <HeaderGroup>
            <Title>
              Contrato #{contrato.id}{' '}
              <StatusBadge $status={contrato.status}>{contrato.status?.toLowerCase()}</StatusBadge>
            </Title>
            <Subtitle>
              Cliente {contrato.cliente_id} • Plano congelado: {contrato.nome_plano}
            </Subtitle>
          </HeaderGroup>
          <ActionBar>
            <GhostButton onClick={() => navigate('/gestao/contratos')}>
              <FiArrowLeft />
              Voltar
            </GhostButton>
            <PrimaryButton onClick={exportarParaPDF}>
              <FiDownload />
              Exportar PDF
            </PrimaryButton>
            <PrimaryButton onClick={exportarTodosContratosCliente} disabled={exportandoTodos}>
              <FiArchive />
              {exportandoTodos ? 'Exportando...' : 'Exportar Todos os Contratos'}
            </PrimaryButton>
            {permiteEdicao && (
              <GhostButton onClick={() => navigate(`/gestao/contratos/${contrato.id}/editar`)}>
                <FiEdit />
                Editar contrato
              </GhostButton>
            )}
          </ActionBar>
        </Header>

        <Grid>
          <Section>
            <Card>
              <CardHeader>
                <CardTitle>
                  <FiClipboard />
                  Resumo do contrato
                </CardTitle>
                <Badge>
                  <FiShield />
                  Criado em {formatDate(contrato.created_at)}
                </Badge>
              </CardHeader>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>
                    <FiDollarSign />
                    Valor mensal
                  </InfoLabel>
                  <InfoValue>{formatCurrency(contrato.valor_mensal)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiLayers />
                    Créditos mensais
                  </InfoLabel>
                  <InfoValue>{contrato.creditos_mensais}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiCalendar />
                    Início
                  </InfoLabel>
                  <InfoValue>{formatDate(contrato.data_inicio)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiCalendar />
                    Fim
                  </InfoLabel>
                  <InfoValue>
                    {contrato.data_fim ? formatDate(contrato.data_fim) : 'Sem término'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiClock />
                    Renovação
                  </InfoLabel>
                  <InfoValue>{contrato.renovacao_meses} meses</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiZap />
                    Saldo atual
                  </InfoLabel>
                  <InfoValue>
                    {contrato.ciclo_atual
                      ? `${contrato.ciclo_atual.creditos_saldo} créditos`
                      : 'Sem ciclo ativo'}
                  </InfoValue>
                </InfoItem>
              </InfoGrid>
            </Card>
          </Section>

          <Section>
            <Card>
              <CardHeader>
                <CardTitle>
                  <FiBarChart2 />
                  Ciclos mensais
                </CardTitle>
                <ActionBar>
                  {permiteEdicao && (
                    <PrimaryButton
                      onClick={() =>
                        setModalCiclo({
                          aberto: true,
                          inicio: '',
                          fim: '',
                          creditos_base: contrato.creditos_mensais || '',
                          creditos_carryover: 0,
                          erro: null,
                          salvando: false,
                        })
                      }
                    >
                      <FiPlus />
                      Criar ciclo manual
                    </PrimaryButton>
                  )}
                </ActionBar>
              </CardHeader>
              {contrato.ciclos && contrato.ciclos.length > 0 ? (
                <Timeline>
                  {contrato.ciclos
                    .slice()
                    .sort((a, b) => new Date(b.inicio).getTime() - new Date(a.inicio).getTime())
                    .map((ciclo) => (
                      <CycleItem
                        key={ciclo.id}
                        $active={selectedCycleId === ciclo.id}
                        onClick={() => setSelectedCycleId(ciclo.id)}
                      >
                        <CycleTitle>
                          Ciclo {formatDate(ciclo.inicio)} - {formatDate(ciclo.fim)}
                        </CycleTitle>
                        <CycleSubtitle>
                          <span>Saldo {ciclo.creditos_saldo} / {ciclo.creditos_totais} créditos</span>
                          {ciclo.cobranca && (
                            <ExtensaoStatus $status={ciclo.cobranca?.status || 'PENDING'}>
                              {getCobrancaStatusLabel(ciclo.cobranca?.status)}
                            </ExtensaoStatus>
                          )}
                        </CycleSubtitle>
                      </CycleItem>
                    ))}
                </Timeline>
              ) : (
                <EmptyState>
                  <EmptyIcon>
                    <FiInbox />
                  </EmptyIcon>
                  <span>Este contrato ainda não possui ciclos registrados.</span>
                  {permiteEdicao && (
                    <PrimaryButton
                      onClick={() =>
                        setModalCiclo({
                          aberto: true,
                          inicio: '',
                          fim: '',
                          creditos_base: contrato.creditos_mensais || '',
                          creditos_carryover: 0,
                          erro: null,
                          salvando: false,
                        })
                      }
                    >
                      Criar primeiro ciclo
                    </PrimaryButton>
                  )}
                </EmptyState>
              )}
            </Card>
          </Section>
        </Grid>

        {cicloSelecionado && (
          <Card>
            <CardHeader>
              <CardTitle>
                <FiRadio />
                Ciclo selecionado {formatDate(cicloSelecionado.inicio)} -{' '}
                {formatDate(cicloSelecionado.fim)}
              </CardTitle>
            </CardHeader>

            <CycleDetails>
              <MetricCard>
                <MetricLabel>Créditos base</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_base}</MetricValue>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Extensões</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_extensoes}</MetricValue>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Carryover</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_carryover}</MetricValue>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Créditos totais</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_totais}</MetricValue>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Utilizados</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_usados}</MetricValue>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Saldo</MetricLabel>
                <MetricValue>{cicloSelecionado.creditos_saldo}</MetricValue>
              </MetricCard>
            </CycleDetails>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FiDollarSign />
                  Cobrança do ciclo
                </CardTitle>
                {permiteEdicao && !cicloSelecionado?.cobranca && (
                  <ActionBar>
                    <PrimaryButton
                      onClick={() => gerarCobrancaCicloManual(
                        contrato?.id,
                        cicloSelecionado?.id,
                        setGerandoCobrancaCiclo,
                        carregarContrato
                      )}
                      disabled={gerandoCobrancaCiclo || !contrato?.id || !cicloSelecionado?.id}
                      title="Gerar cobrança para este ciclo"
                    >
                      {gerandoCobrancaCiclo ? (
                        <>
                          <FiClock style={{ animation: 'spin 1s linear infinite' }} />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <FiPlus />
                          Gerar Cobrança
                        </>
                      )}
                    </PrimaryButton>
                  </ActionBar>
                )}
              </CardHeader>
              {cicloSelecionado?.cobranca ? (
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Data</Th>
                        <Th>Créditos</Th>
                        <Th>Valor</Th>
                        <Th>Status</Th>
                        <Th>Ações</Th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <Td>{formatDate(cicloSelecionado.cobranca?.due_date)}</Td>
                        <Td>{cicloSelecionado.creditos_base || 0}</Td>
                        <Td>{formatCurrency(cicloSelecionado.cobranca?.valor || 0)}</Td>
                        <Td>
                          <ExtensaoStatus $status={cicloSelecionado.cobranca?.status || 'PENDING'}>
                            {getCobrancaStatusLabel(cicloSelecionado.cobranca?.status)}
                          </ExtensaoStatus>
                        </Td>
                        <Td>
                          <TableActions>
                            {(() => {
                              const statusCobranca = (cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase();
                              const pagamentoConfirmado = statusCobranca === 'RECEIVED' || statusCobranca === 'CONFIRMED' || statusCobranca === 'CONFIRMADO';
                              
                              return (
                                <>
                                  <ActionButton
                                    onClick={() => baixarPDF(cicloSelecionado.cobranca?.bank_slip_url)}
                                    disabled={pagamentoConfirmado || !cicloSelecionado.cobranca?.bank_slip_url}
                                    title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Baixar PDF do boleto'}
                                  >
                                    <FiDownload />
                                  </ActionButton>
                                  <ActionButton
                                    onClick={() => copiarConteudo(cicloSelecionado.cobranca?.boleto_linha_digitavel, 'Código de barras')}
                                    disabled={pagamentoConfirmado || !cicloSelecionado.cobranca?.boleto_linha_digitavel}
                                    title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código de barras do boleto'}
                                  >
                                    <FaBarcode />
                                  </ActionButton>
                                  <ActionButton
                                    onClick={() => {
                                      const pixPayload = extrairPayloadPix(cicloSelecionado.cobranca?.pix_qr_code) || cicloSelecionado.cobranca?.pix_payload;
                                      copiarConteudo(pixPayload, 'Código Pix');
                                    }}
                                    disabled={pagamentoConfirmado || (!extrairPayloadPix(cicloSelecionado.cobranca?.pix_qr_code) && !cicloSelecionado.cobranca?.pix_payload)}
                                    title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código Pix'}
                                  >
                                    <MdPix />
                                  </ActionButton>
                                </>
                              );
                            })()}
                            <ActionButton
                              onClick={() => {
                                if (!cicloSelecionado.cobranca?.id) {
                                  toast.error('Cobrança não encontrada');
                                  return;
                                }
                                // Abrir modal para solicitar valor e data
                                // Data padrão: 1 dia após a data atual
                                const amanha = new Date();
                                amanha.setDate(amanha.getDate() + 1);
                                const dataPadrao = amanha.toISOString().split('T')[0];
                                const valorPadrao = cicloSelecionado.cobranca.valor || '';
                                setModalConfirmarPagamento({
                                  aberto: true,
                                  cobranca: cicloSelecionado.cobranca,
                                  valor: valorPadrao,
                                  data: dataPadrao,
                                  salvando: false,
                                  erro: null,
                                });
                              }}
                              disabled={(cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'PENDING' || !cicloSelecionado.cobranca?.id}
                              title={
                                (cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'PENDING'
                                  ? 'Apenas cobranças pendentes podem ter pagamento confirmado manualmente'
                                  : 'Confirmar pagamento manual'
                              }
                            >
                              <FiCheckCircle />
                            </ActionButton>
                            <ActionButton
                              onClick={() => {
                                if (!cicloSelecionado.cobranca?.id) {
                                  toast.error('Cobrança não encontrada');
                                  return;
                                }
                                // Abrir modal de confirmação
                                setModalExcluirCobranca({
                                  aberto: true,
                                  cobranca: cicloSelecionado.cobranca,
                                  excluindo: false,
                                });
                              }}
                              disabled={
                                ((cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'PENDING' &&
                                  (cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'OVERDUE') ||
                                !cicloSelecionado.cobranca?.id
                              }
                              title={
                                (cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'PENDING' &&
                                (cicloSelecionado.cobranca?.status || 'PENDING').toUpperCase() !== 'OVERDUE'
                                  ? 'Apenas cobranças pendentes ou atrasadas podem ser excluídas'
                                  : 'Excluir cobrança'
                              }
                            >
                              <FiTrash2 />
                            </ActionButton>
                          </TableActions>
                        </Td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
              ) : (
                <EmptyState>
                  <EmptyIcon>
                    <FiInbox />
                  </EmptyIcon>
                  <span>Nenhuma cobrança registrada para este ciclo.</span>
                </EmptyState>
              )}
            </Card>
          </Card>
        )}

        {cicloSelecionado && (
          <Card>
            <CardHeader>
              <CardTitle>
                <FiFilter />
                Extensões do ciclo
              </CardTitle>
              <ActionBar>
                {permiteEdicao && (
                  <PrimaryButton
                    onClick={() =>
                      setModalExtensao({
                        ...criarEstadoModalExtensao(),
                        aberto: true,
                        cicloId: cicloSelecionado.id,
                      })
                    }
                    disabled={cicloSelecionado.encerrado}
                    title={cicloSelecionado.encerrado ? 'Não é possível adicionar extensão a um ciclo já encerrado' : 'Registrar extensão'}
                  >
                    <FiPlus />
                    Registrar extensão
                  </PrimaryButton>
                )}
              </ActionBar>
            </CardHeader>
            {cicloSelecionado.extensoes && cicloSelecionado.extensoes.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Data</Th>
                      <Th>Créditos</Th>
                      <Th>Valor</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {cicloSelecionado.extensoes.map((extensao) => {
                      const cobranca = extensao.cobranca;
                      const bankSlipUrl = cobranca?.bank_slip_url;
                      const boletoLinha = cobranca?.boleto_linha_digitavel;
                      const pixPayload = extrairPayloadPix(cobranca?.pix_qr_code) || cobranca?.pix_payload;
                      // Se há cobrança, priorizar o status da cobrança, senão usar status da extensão
                      const temCobranca = !!cobranca;
                      const statusCobranca = cobranca?.status;
                      const statusExtensao = extensao.status;
                      // Para exibição visual (cor da tag), usar status da cobrança se existir, senão da extensão
                      const statusParaExibir = statusCobranca || statusExtensao || 'PENDING';
                      // Para o label, se há cobrança, usar status da cobrança (mesmo que seja null/undefined, usar da extensão como fallback)
                      // Se não há cobrança, usar status da extensão
                      const statusParaLabel = temCobranca 
                        ? (statusCobranca || statusExtensao || 'PENDING')
                        : (statusExtensao || 'PENDING');
                      
                      return (
                        <tr key={extensao.id}>
                          <Td>{formatDate(extensao.data)}</Td>
                          <Td>{extensao.creditos}</Td>
                          <Td>{formatCurrency(extensao.valor_pago)}</Td>
                          <Td>
                            <ExtensaoStatus $status={statusParaExibir}>
                              {getExtensaoStatusLabel(statusParaLabel, temCobranca)}
                            </ExtensaoStatus>
                          </Td>
                          <Td>
                            <TableActions>
                              {(() => {
                                const statusNormalizado = statusParaExibir.toUpperCase();
                                const pagamentoConfirmado = statusNormalizado === 'RECEIVED' || statusNormalizado === 'CONFIRMED' || statusNormalizado === 'CONFIRMADO';
                                
                                return (
                                  <>
                                    <ActionButton
                                      onClick={() => baixarPDF(bankSlipUrl)}
                                      disabled={pagamentoConfirmado || !bankSlipUrl}
                                      title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Baixar PDF do boleto'}
                                    >
                                      <FiDownload />
                                    </ActionButton>
                                    <ActionButton
                                      onClick={() => copiarConteudo(boletoLinha, 'Código de barras')}
                                      disabled={pagamentoConfirmado || !boletoLinha}
                                      title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código de barras do boleto'}
                                    >
                                      <FaBarcode />
                                    </ActionButton>
                                    <ActionButton
                                      onClick={() => copiarConteudo(pixPayload, 'Código Pix')}
                                      disabled={pagamentoConfirmado || !pixPayload}
                                      title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código Pix'}
                                    >
                                      <MdPix />
                                    </ActionButton>
                                  </>
                                );
                              })()}
                              <ActionButton
                                onClick={() => {
                                  if (!cobranca?.id) {
                                    toast.error('Cobrança não encontrada');
                                    return;
                                  }
                                  // Abrir modal para solicitar valor e data
                                  // Data padrão: 1 dia após a data atual
                                  const amanha = new Date();
                                  amanha.setDate(amanha.getDate() + 1);
                                  const dataPadrao = amanha.toISOString().split('T')[0];
                                  const valorPadrao = cobranca.valor || extensao.valor_pago || '';
                                  setModalConfirmarPagamento({
                                    aberto: true,
                                    cobranca,
                                    valor: valorPadrao,
                                    data: dataPadrao,
                                    salvando: false,
                                    erro: null,
                                  });
                                }}
                                disabled={statusParaExibir.toUpperCase() !== 'PENDING' || !cobranca?.id}
                                title={
                                  statusParaExibir.toUpperCase() !== 'PENDING'
                                    ? 'Apenas cobranças pendentes podem ter pagamento confirmado manualmente'
                                    : 'Confirmar pagamento manual'
                                }
                              >
                                <FiCheckCircle />
                              </ActionButton>
                              <ActionButton
                                onClick={() => {
                                  if (!cobranca?.id) {
                                    toast.error('Cobrança não encontrada');
                                    return;
                                  }
                                  // Abrir modal de confirmação
                                  setModalExcluirCobranca({
                                    aberto: true,
                                    cobranca,
                                    excluindo: false,
                                  });
                                }}
                                disabled={
                                  (statusParaExibir.toUpperCase() !== 'PENDING' &&
                                    statusParaExibir.toUpperCase() !== 'OVERDUE') ||
                                  !cobranca?.id
                                }
                                title={
                                  statusParaExibir.toUpperCase() !== 'PENDING' &&
                                  statusParaExibir.toUpperCase() !== 'OVERDUE'
                                    ? 'Apenas cobranças pendentes ou atrasadas podem ser excluídas'
                                    : 'Excluir cobrança'
                                }
                              >
                                <FiTrash2 />
                              </ActionButton>
                            </TableActions>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <FiInbox />
                </EmptyIcon>
                <span>Nenhuma extensão registrada.</span>
              </EmptyState>
            )}
          </Card>
        )}
      </Container>

      {modalCiclo.aberto && (
        <ModalOverlay onClick={() => setModalCiclo({ aberto: false, inicio: '', fim: '', creditos_base: '', creditos_carryover: 0, erro: null, salvando: false })}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Criar ciclo manual</ModalTitle>
              <CloseButton
                onClick={() =>
                  setModalCiclo({ aberto: false, inicio: '', fim: '', creditos_base: '', creditos_carryover: 0, erro: null, salvando: false })
                }
              >
                ×
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Início *</Label>
                <Input
                  type="date"
                  value={modalCiclo.inicio}
                  onChange={(e) => setModalCiclo((prev) => ({ ...prev, inicio: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Fim *</Label>
                <Input
                  type="date"
                  value={modalCiclo.fim}
                  onChange={(e) => setModalCiclo((prev) => ({ ...prev, fim: e.target.value }))}
                  min={modalCiclo.inicio || undefined}
                />
              </FormGroup>
              <FormGroup>
                <Label>Créditos base *</Label>
                <Input
                  type="number"
                  min="0"
                  value={modalCiclo.creditos_base}
                  onChange={(e) => setModalCiclo((prev) => ({ ...prev, creditos_base: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Créditos carryover</Label>
                <Input
                  type="number"
                  min="0"
                  value={modalCiclo.creditos_carryover}
                  onChange={(e) =>
                    setModalCiclo((prev) => ({ ...prev, creditos_carryover: e.target.value }))
                  }
                />
              </FormGroup>
              {modalCiclo.erro && (
                <Alert $type="error">
                  <FiAlertTriangle />
                  {modalCiclo.erro}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <GhostButton
                onClick={() =>
                  setModalCiclo({ aberto: false, inicio: '', fim: '', creditos_base: '', creditos_carryover: 0, erro: null, salvando: false })
                }
              >
                Cancelar
              </GhostButton>
              <PrimaryButton onClick={criarCiclo} disabled={modalCiclo.salvando}>
                {modalCiclo.salvando ? 'Criando...' : 'Criar ciclo'}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {modalExtensao.aberto && (
        <ModalOverlay onClick={fecharModalExtensao}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Gerar cobrança de extensão</ModalTitle>
              <CloseButton onClick={fecharModalExtensao}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {modalExtensao.resultado ? (
                <ResultadoWrapper>
                  <ResultadoBox>
                    <ResultadoLine>
                      <ResultadoLabel>Contrato</ResultadoLabel>
                      <ResultadoValue>Contrato #{contrato?.id}</ResultadoValue>
                    </ResultadoLine>
                    {cicloSelecionado && (
                      <ResultadoLine>
                        <ResultadoLabel>Ciclo</ResultadoLabel>
                        <ResultadoValue>
                          {formatDate(cicloSelecionado.inicio)} - {formatDate(cicloSelecionado.fim)}
                        </ResultadoValue>
                      </ResultadoLine>
                    )}
                    <ResultadoLine>
                      <ResultadoLabel>Status</ResultadoLabel>
                      <ResultadoValue>
                        {getCobrancaStatusLabel(modalExtensao.resultado.status)}
                      </ResultadoValue>
                    </ResultadoLine>
                    <ResultadoLine>
                      <ResultadoLabel>Valor da cobrança</ResultadoLabel>
                      <ResultadoValue>{formatCurrency(modalExtensao.resultado.valor)}</ResultadoValue>
                    </ResultadoLine>
                  </ResultadoBox>

                  {modalExtensao.formaPagamento === 'boleto_pix' && (
                    <>
                      <ResultadoBox>
                        <ResultadoLabel>Linha digitável</ResultadoLabel>
                        {modalExtensao.resultado.linhaDigitavel ? (
                          <CopyField>
                            <span>{modalExtensao.resultado.linhaDigitavel}</span>
                            <CopyButtonSmall
                              type="button"
                              onClick={() =>
                                copiarConteudo(modalExtensao.resultado.linhaDigitavel, 'Linha digitável')
                              }
                              title="Copiar código de barras"
                            >
                              <FaBarcode />
                            </CopyButtonSmall>
                          </CopyField>
                        ) : (
                          <span style={{ color: theme.colors.text.muted }}>
                            Aguardando geração da linha digitável...
                          </span>
                        )}
                      </ResultadoBox>

                      <ResultadoBox>
                        <ResultadoLabel>Pix</ResultadoLabel>
                        <PixWrapper>
                          {getPixImageSrc(modalExtensao.resultado.pixQrCode) ? (
                            <PixImage
                              src={getPixImageSrc(modalExtensao.resultado.pixQrCode)}
                              alt="QRCode Pix"
                            />
                          ) : (
                            <span style={{ color: theme.colors.text.muted }}>
                              QR Code disponível diretamente na invoice.
                            </span>
                          )}
                          {modalExtensao.resultado.pixPayload && (
                            <PixInfo>
                              <CopyField>
                                <span>{modalExtensao.resultado.pixPayload}</span>
                                <CopyButtonSmall
                                  type="button"
                                  onClick={() =>
                                    copiarConteudo(modalExtensao.resultado.pixPayload, 'Código Pix')
                                  }
                                  title="Copiar código Pix"
                                >
                                  <MdPix />
                                </CopyButtonSmall>
                              </CopyField>
                            </PixInfo>
                          )}
                        </PixWrapper>
                      </ResultadoBox>

                      {linkPagamentoModalExtensao && (
                        <InvoiceLink href={linkPagamentoModalExtensao} target="_blank" rel="noreferrer" title="Baixar PDF">
                          <FiDownload />
                          Baixar PDF
                        </InvoiceLink>
                      )}

                      <Alert $type="info">
                        <FiInfo />
                        Após a confirmação do pagamento pelo Asaas, os créditos serão aplicados
                        automaticamente ao ciclo selecionado.
                      </Alert>
                    </>
                  )}

                  {modalExtensao.formaPagamento === 'cartao_credito' && (
                    <Alert $type="success">
                      <FiInfo />
                      Pagamento via cartão de crédito processado. Os créditos foram aplicados automaticamente ao ciclo selecionado.
                    </Alert>
                  )}
                </ResultadoWrapper>
              ) : (
                <>
                  <FormGroup>
                    <Label>Créditos adicionais *</Label>
                    <Select
                      value={modalExtensao.creditos}
                      onChange={(e) => setModalExtensao((prev) => ({ ...prev, creditos: e.target.value }))}
                    >
                      <option value="">Selecione a quantidade</option>
                      {opcoesCreditos.map((valor) => (
                        <option key={valor} value={valor}>
                          {valor} créditos
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Valor estimado</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={modalExtensao.valor_pago}
                      readOnly
                      style={{ cursor: 'not-allowed', opacity: 0.8 }}
                      title="Valor calculado automaticamente com base nos créditos selecionados"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Forma de pagamento *</Label>
                    <Select
                      value={modalExtensao.formaPagamento}
                      onChange={(e) => setModalExtensao((prev) => ({ ...prev, formaPagamento: e.target.value }))}
                    >
                      <option value="cartao_credito">Cartão de Crédito</option>
                      <option value="boleto_pix">Boleto/Pix</option>
                    </Select>
                  </FormGroup>
                  {modalExtensao.formaPagamento === 'boleto_pix' && (
                    <Alert $type="info">
                      <FiInfo />
                      Caso pague no boleto, pode demorar até 3 dias para compensar o pagamento. Os créditos serão contabilizados após a compensação do boleto.
                    </Alert>
                  )}
                  {modalExtensao.erro && (
                    <Alert $type="error">
                      <FiAlertTriangle />
                      {modalExtensao.erro}
                    </Alert>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              {modalExtensao.resultado ? (
                <>
                  {modalExtensao.formaPagamento === 'boleto_pix' ? (
                    <>
                      <GhostButton onClick={fecharModalExtensao}>Fechar</GhostButton>
                      <PrimaryButton
                        onClick={() =>
                          linkPagamentoModalExtensao && window.open(linkPagamentoModalExtensao, '_blank')
                        }
                        disabled={!linkPagamentoModalExtensao}
                      >
                        Abrir fatura
                      </PrimaryButton>
                    </>
                  ) : (
                    <PrimaryButton onClick={fecharModalExtensao}>Fechar</PrimaryButton>
                  )}
                </>
              ) : (
                <>
                  <GhostButton onClick={fecharModalExtensao}>Cancelar</GhostButton>
                  <PrimaryButton onClick={adicionarExtensao} disabled={modalExtensao.salvando}>
                    {modalExtensao.salvando ? 'Gerando...' : 'Gerar cobrança'}
                  </PrimaryButton>
                </>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de confirmação de pagamento manual */}
      {modalConfirmarPagamento.aberto && (
        <ModalOverlay onClick={fecharModalConfirmarPagamento}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirmar pagamento manual</ModalTitle>
              <CloseButton onClick={fecharModalConfirmarPagamento}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Valor pago *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={modalConfirmarPagamento.valor}
                  onChange={(e) =>
                    setModalConfirmarPagamento((prev) => ({ ...prev, valor: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </FormGroup>
              <FormGroup>
                <Label>Data do pagamento *</Label>
                <Input
                  type="date"
                  value={modalConfirmarPagamento.data}
                  onChange={(e) =>
                    setModalConfirmarPagamento((prev) => ({ ...prev, data: e.target.value }))
                  }
                />
              </FormGroup>
              {modalConfirmarPagamento.erro && (
                <Alert $type="error">
                  <FiAlertTriangle />
                  {modalConfirmarPagamento.erro}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <GhostButton onClick={fecharModalConfirmarPagamento} disabled={modalConfirmarPagamento.salvando}>
                Cancelar
              </GhostButton>
              <PrimaryButton
                onClick={async () => {
                  if (!modalConfirmarPagamento.cobranca?.id) {
                    setModalConfirmarPagamento((prev) => ({
                      ...prev,
                      erro: 'Cobrança não encontrada',
                    }));
                    return;
                  }

                  const valor = Number.parseFloat(modalConfirmarPagamento.valor);
                  if (!Number.isFinite(valor) || valor <= 0) {
                    setModalConfirmarPagamento((prev) => ({
                      ...prev,
                      erro: 'Valor deve ser maior que zero',
                    }));
                    return;
                  }

                  if (!modalConfirmarPagamento.data) {
                    setModalConfirmarPagamento((prev) => ({
                      ...prev,
                      erro: 'Data do pagamento é obrigatória',
                    }));
                    return;
                  }

                  setModalConfirmarPagamento((prev) => ({ ...prev, salvando: true, erro: null }));

                  try {
                    const isCobrancaCiclo = !modalConfirmarPagamento.cobranca.creditos_extensao;
                    const logPrefix = isCobrancaCiclo ? '[CICLO]' : '[EXTENSAO]';
                    console.log(`${logPrefix} Confirmando pagamento manual`, {
                      cobrancaId: modalConfirmarPagamento.cobranca.id,
                      asaasPaymentId: modalConfirmarPagamento.cobranca.asaas_payment_id,
                      valor,
                      data: modalConfirmarPagamento.data,
                      tipo: isCobrancaCiclo ? 'ciclo' : 'extensao',
                    });
                    const response = isCobrancaCiclo
                      ? await contratosAPI.confirmarPagamentoCiclo(
                          modalConfirmarPagamento.cobranca.id,
                          {
                            value: valor,
                            paymentDate: modalConfirmarPagamento.data,
                            notifyCustomer: true,
                          }
                        )
                      : await contratosAPI.confirmarPagamentoExtensao(
                          modalConfirmarPagamento.cobranca.id,
                          {
                            value: valor,
                            paymentDate: modalConfirmarPagamento.data,
                            notifyCustomer: true,
                          }
                        );
                    console.log(`${logPrefix} Pagamento confirmado`, response.data);
                    toast.success('Pago com sucesso!');
                    fecharModalConfirmarPagamento();
                    // Recarregar dados após confirmação
                    await carregarContrato(true);
                  } catch (error) {
                    console.error('Erro ao confirmar pagamento', {
                      error,
                      response: error.response,
                      data: error.response?.data,
                      status: error.response?.status,
                    });
                    const errorMessage =
                      error.response?.data?.error ||
                      error.response?.data?.details ||
                      error.message ||
                      'Erro ao confirmar pagamento';
                    setModalConfirmarPagamento((prev) => ({
                      ...prev,
                      erro: errorMessage,
                      salvando: false,
                    }));
                  }
                }}
                disabled={modalConfirmarPagamento.salvando}
              >
                {modalConfirmarPagamento.salvando ? 'Confirmando...' : 'Confirmar pagamento'}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de confirmação de exclusão de cobrança */}
      {modalExcluirCobranca.aberto && (
        <ModalOverlay onClick={fecharModalExcluirCobranca}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalTitle style={{ color: theme.colors.error[500] }}>Excluir Cobrança</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir esta cobrança? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • A cobrança {modalExcluirCobranca.cobranca?.creditos_extensao ? 'de extensão' : 'do ciclo'}
              <br />
              • O pagamento no Asaas
              <br />
              • Todos os dados relacionados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={fecharModalExcluirCobranca} disabled={modalExcluirCobranca.excluindo}>
                Cancelar
              </ModalButton>
              <ModalButton
                $danger
                onClick={async () => {
                  if (!modalExcluirCobranca.cobranca?.id) {
                    toast.error('Cobrança não encontrada');
                    return;
                  }

                  setModalExcluirCobranca((prev) => ({ ...prev, excluindo: true }));

                  try {
                    const isCobrancaCiclo = !modalExcluirCobranca.cobranca.creditos_extensao;
                    const logPrefix = isCobrancaCiclo ? '[CICLO]' : '[EXTENSAO]';
                    console.log(`${logPrefix} Excluindo cobrança`, {
                      cobrancaId: modalExcluirCobranca.cobranca.id,
                      asaasPaymentId: modalExcluirCobranca.cobranca.asaas_payment_id,
                      status: modalExcluirCobranca.cobranca.status,
                      tipo: isCobrancaCiclo ? 'ciclo' : 'extensao',
                    });
                    await (isCobrancaCiclo
                      ? contratosAPI.excluirCobrancaCiclo(modalExcluirCobranca.cobranca.id)
                      : contratosAPI.excluirCobrancaExtensao(modalExcluirCobranca.cobranca.id));
                    console.log(`${logPrefix} Cobrança excluída com sucesso`);
                    toast.success('Cobrança excluída com sucesso!');
                    fecharModalExcluirCobranca();
                    // Recarregar dados após exclusão
                    await carregarContrato(true);
                  } catch (error) {
                    console.error('Erro ao excluir cobrança', {
                      error,
                      response: error.response,
                      data: error.response?.data,
                      status: error.response?.status,
                    });
                    const errorMessage =
                      error.response?.data?.error ||
                      error.response?.data?.details ||
                      error.message ||
                      'Erro ao excluir cobrança';
                    toast.error(errorMessage);
                    setModalExcluirCobranca((prev) => ({ ...prev, excluindo: false }));
                  }
                }}
                disabled={modalExcluirCobranca.excluindo}
              >
                <FiTrash2 />
                {modalExcluirCobranca.excluindo ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default ContratoDetalhes;


